import { cn } from "@/lib/utils";
import { useDraggable } from "@dnd-kit/core";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { badgeVariants } from "../ui/badge";
import { Button } from "../ui/button";
import { RecipientField } from "./recipients";

export type RecipientDragData = {
    email: string;
    field: RecipientField;
};

export const recipientChipClassName = cn(
    badgeVariants(),
    "h-[27px] select-none border border-primary-lighter bg-white text-sm text-text-subtitle",
    "transition-[background-color,border-color,color,box-shadow,opacity] duration-200 ease-out",
);

type RecipientChipProps = {
    email: string;
    field?: RecipientField;
    onRemove: (email: string) => void;
};

const RecipientChip = ({ email, field, onRemove }: RecipientChipProps) => {
    const { t } = useTranslation();
    const { setNodeRef, listeners, isDragging } = useDraggable({
        id: `${field}:${email}`,
        data: { email, field },
        disabled: !field,
    });

    return (
        <div
            ref={setNodeRef}
            className={cn(
                recipientChipClassName,
                "animate-in fade-in-0 zoom-in-90 duration-200",
                field &&
                    "touch-none cursor-grab hover:border-primary-light hover:bg-surface-activeFg hover:shadow-sm",
                isDragging &&
                    "border-dashed border-primary-light bg-surface-activeFg text-primary-light shadow-none",
            )}
            {...listeners}
        >
            {email}
            <Button
                aria-label={t("common.remove")}
                className={cn(
                    "ml-1 hover:text-destructive",
                    isDragging && "invisible",
                )}
                onClick={(e) => {
                    e.stopPropagation();
                    onRemove(email);
                }}
                onFocus={(e) => {
                    e.stopPropagation();
                }}
                size="tinyCircle"
                variant="ghost"
            >
                <X className="h-3 w-3" />
            </Button>
        </div>
    );
};

export default RecipientChip;
